import { z } from "zod"

// A marketplace id is a single OPAQUE token identifying one listing channel —
// a brand on a marketplace in one country (e.g. "decathlon-it", "decathlon-fr",
// "shop-apotheke-de", "cdiscount-fr"). It is lowercase kebab-case and carries
// no internal structure the code is allowed to crack open: everything about a
// marketplace (its country/locale, brand, Mirakl credentials, shop id, schema
// source) lives in that marketplace's own config, keyed by this id — never
// derived by splitting the string. The id travels WHOLE through the DB, URLs,
// query keys, JSON, and the CLI. This module is the single owner of its grammar.

const MARKETPLACE_ID_PATTERN = /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/
const COUNTRY_PATTERN = /^[a-z]{2}$/

// ISO 3166-1 alpha-2 country code (lowercase), SHAPE only. For country codes
// parsed out of external (Mirakl) payloads — an order's shipping address can name
// any country on earth, so membership in our own canonical set is the wrong test.
// NOT to decompose a marketplace id, and NOT for the countries we declare on our
// own records (store registry, marketplace config): those use
// `canonicalCountrySchema` below.
export const countrySchema = z
  .string()
  .regex(COUNTRY_PATTERN, "country must be ISO 3166-1 alpha-2 lowercase (two letters)")

export type Country = z.infer<typeof countrySchema>

// The CANONICAL alpha-2 countries Nova declares its own records under. Shape
// validation is not enough for a field we own: `cs`, `el`, `sl` and `sr` are the
// LANGUAGE codes of Czechia, Greece, Slovenia and Serbia, they all pass
// `/^[a-z]{2}$/`, and three of them are even real ISO countries for somewhere
// else (`sl` Sierra Leone, `sr` Suriname, `cs` a withdrawn code) — so a
// language slipping into a country field would validate and then resolve the
// wrong market's data. Membership in this list is what rejects that.
//
// Deliberately narrow: the European markets we plausibly serve, not all of ISO
// 3166-1. In practice every one of them is already here, so a new market is far
// more likely to need nothing than to need an entry — check before assuming.
//
// `COUNTRY_FLAG_COUNTRIES` in `@nova-ai/product-visual-templates` happens to hold
// the same codes today, and that is a COINCIDENCE of both covering the European
// markets, not a coupling: one answers "is this a country we may declare a record
// under", the other "do we have flag art for it". They are independent by design —
// that package is generated by `make vendor-flags` and `packages/shared` must not
// depend on it — so do NOT sync one to the other or add a drift test between them.
//
// NOT for country codes parsed out of external payloads (Mirakl order shipping
// addresses can name any country on earth) — those keep `countrySchema`.
export const COUNTRY_CODES = [
  "at",
  "be",
  "bg",
  "ch",
  "cy",
  "cz",
  "de",
  "dk",
  "ee",
  "es",
  "fi",
  "fr",
  "gb",
  "gr",
  "hr",
  "hu",
  "ie",
  "is",
  "it",
  "li",
  "lt",
  "lu",
  "lv",
  "mt",
  "nl",
  "no",
  "pl",
  "pt",
  "ro",
  "rs",
  "se",
  "si",
  "sk",
] as const

export type CanonicalCountry = (typeof COUNTRY_CODES)[number]

/** A country on a record Nova owns (store registry, marketplace config).
 *  Rejects language codes that merely LOOK like countries — see `COUNTRY_CODES`. */
export const canonicalCountrySchema = z.enum(COUNTRY_CODES)

// Branded so a bare string can't masquerade as a validated marketplace id.
// Use it directly as a field in any Zod schema: `marketplace: marketplaceIdSchema`.
export const marketplaceIdSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(MARKETPLACE_ID_PATTERN, "marketplace id must be lowercase kebab-case, e.g. 'decathlon-it'")
  .brand<"MarketplaceId">()

export type MarketplaceId = z.infer<typeof marketplaceIdSchema>

// Validate untrusted input (URL params, CLI args, request bodies) into an id.
export function parseMarketplaceId(input: string): MarketplaceId {
  const result = marketplaceIdSchema.safeParse(input)
  if (!result.success) {
    throw new Error(
      `Invalid marketplace id ${JSON.stringify(input)}: ${result.error.issues
        .map((i) => i.message)
        .join("; ")}`
    )
  }
  return result.data
}

// Cheap cast for strings we already trust (e.g. values read back from our own
// DB). Skips re-validation; do not use on external input — use parse* instead.
export function asMarketplaceId(value: string): MarketplaceId {
  return value as MarketplaceId
}
